/**
 * Typed calls for the real `/v2` Payouts API surface (the ~12 endpoints that exist today).
 * Every function takes a {@link RequestContext} ({ env, apiKey }) so the in-app environment
 * switcher decides which deployment is hit. Unbacked portal features (logs, events, webhooks,
 * key management, cross-business payment/delivery lists, aggregates) have no function here —
 * see docs/payouts-portal-api-todo.md.
 *
 * Source of truth: src/apps/external/src/v2/payouts/payouts.api.external.yaml
 */
import { http, portalHttp, request, type RequestContext } from './client';
import type { AddressSuggestion } from './lookup';
import type { Business, SignupRequest, SignupResponse } from './types';
import { getSonarSessionToken } from '@/lib/sonarSdk';

const sonarHeader = (sonarToken?: string): Record<string, string> => (sonarToken ? { 'Melio-Sonar-Token': sonarToken } : {});

/** Collections may come back as `[...]` or `{ data: [...] }`; tolerate both. */
type ListResponse<T> = T[] | { data: T[] };
const unwrap = <T>(res: ListResponse<T>): T[] => (Array.isArray(res) ? res : (res?.data ?? []));

/** The `me` sentinel resolves any `{businessId}` to the caller's sole business (self-serve). */
export const ME = 'me';

// ---- Portal user auth -------------------------------------------------------------------
export type UserPartner = { partnerName: string; companyName: string };
export type PortalLoginResponse = { email: string; partners: UserPartner[]; accessToken: string };

/** POST /portal/signup — create a portal account and return a session token. */
export async function portalSignup(
  ctx: Pick<RequestContext, 'env'>,
  email: string,
  password: string,
  signal?: AbortSignal
): Promise<{ accessToken: string }> {
  return portalHttp<{ ok: boolean; accessToken: string }>(ctx.env, '/portal/signup', {
    method: 'POST',
    body: { email, password },
    signal,
  });
}

/** POST /portal/login — authenticate and return the user's registered partners. */
export async function portalLogin(
  ctx: Pick<RequestContext, 'env'>,
  email: string,
  password: string,
  signal?: AbortSignal
): Promise<PortalLoginResponse> {
  return portalHttp<PortalLoginResponse>(ctx.env, '/portal/login', {
    method: 'POST',
    body: { email, password },
    signal,
  });
}

export type RegisterPartnerBody = {
  partner: { companyName: string; email: string; password?: string };
  business?: Record<string, unknown>;
};
export type RegisterPartnerResponse = { apiKey: string; partnerName: string; companyName: string };

/**
 * POST /portal/partners — portal backend creates the partner in partnerships-api server-side
 * (admin token stays on the server) and stores the user↔partner association in DynamoDB.
 * Returns the API key once — it is not stored anywhere after this call.
 */
export async function portalRegisterPartner(
  ctx: Pick<RequestContext, 'env'>,
  portalToken: string,
  adminToken: string,
  body: RegisterPartnerBody,
  signal?: AbortSignal
): Promise<RegisterPartnerResponse> {
  return portalHttp<RegisterPartnerResponse>(ctx.env, '/portal/partners', {
    method: 'POST',
    headers: { Authorization: `Bearer ${portalToken}` },
    body: { adminToken, ...body },
    signal,
  });
}

// ---- Signup (admin-gated) ---------------------------------------------------------------
export async function signupPartner(
  env: RequestContext['env'],
  adminToken: string,
  body: SignupRequest,
  signal?: AbortSignal
): Promise<SignupResponse> {
  return http<SignupResponse>(env, '/partners', {
    method: 'POST',
    body,
    headers: { 'x-admin-api-key': adminToken },
    signal,
  });
}

// ---- Entities (a "business" in the portal UI is an entity) ------------------------------
const entityHeader = (entityId: string): Record<string, string> => ({ 'Melio-Entity-Id': entityId });

export async function listBusinesses(ctx: RequestContext, signal?: AbortSignal): Promise<Business[]> {
  return unwrap(await request<ListResponse<Business>>(ctx, '/entities', { signal }));
}

export async function getBusiness(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<Business> {
  return request<Business>(ctx, `/entities/${entityId}`, { signal });
}

export async function patchBusiness(
  ctx: RequestContext,
  entityId: string,
  patch: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Business> {
  return request<Business>(ctx, `/entities/${entityId}`, { method: 'PATCH', body: patch, signal });
}

export type Limitations = { canMakePayments: boolean; missingFields: string[]; blockingReasons: string[] };

/**
 * GET /v2/limitations (Melio-Entity-Id header) — the eligibility / "what's missing" surface.
 * Maps the server's per-capability shape onto the flat form the dashboard renders.
 */
export async function getLimitations(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<Limitations> {
  const res = await request<{ canSendDomesticPayment?: { allowed?: boolean; missingFields?: string[] } }>(ctx, '/limitations', {
    headers: entityHeader(entityId),
    signal,
  });
  const cap = res?.canSendDomesticPayment;
  return { canMakePayments: !!cap?.allowed, missingFields: cap?.missingFields ?? [], blockingReasons: [] };
}

// ---- Funding sources = internal accounts ------------------------------------------------
export async function listFundingSources(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<any[]> {
  return unwrap(await request<ListResponse<any>>(ctx, '/accounts?ownershipType=internal', { headers: entityHeader(entityId), signal }));
}

export async function createFundingSource(
  ctx: RequestContext,
  entityId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<any> {
  return request<any>(ctx, '/accounts', {
    method: 'POST',
    headers: entityHeader(entityId),
    body: { ownershipType: 'internal', ...body },
    signal,
  });
}

// ---- Delivery methods = external accounts (no dedicated list endpoint) ------------------
export async function createDeliveryMethod(
  ctx: RequestContext,
  entityId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<any> {
  return request<any>(ctx, '/accounts', {
    method: 'POST',
    headers: entityHeader(entityId),
    body: { ownershipType: 'external', ...body },
    signal,
  });
}

export async function getDeliveryMethod(ctx: RequestContext, entityId: string, accountId: string, signal?: AbortSignal): Promise<any> {
  return request<any>(ctx, `/accounts/${accountId}`, { headers: entityHeader(entityId), signal });
}

// ---- Payments ---------------------------------------------------------------------------
export async function listPayments(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<any[]> {
  return unwrap(await request<ListResponse<any>>(ctx, '/payments', { headers: entityHeader(entityId), signal }));
}

export async function createPayment(
  ctx: RequestContext,
  entityId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<any> {
  // Payment creation requires an Idempotency-Key header (omitting it returns 400 IDEMPOTENCY_KEY_REQUIRED).
  return request<any>(ctx, '/payments', {
    method: 'POST',
    headers: { ...entityHeader(entityId), 'Idempotency-Key': crypto.randomUUID() },
    body,
    signal,
  });
}

export async function getPayment(ctx: RequestContext, entityId: string, paymentId: string, signal?: AbortSignal): Promise<any> {
  return request<any>(ctx, `/payments/${paymentId}`, { headers: entityHeader(entityId), signal });
}

// ---- Funding source links ---------------------------------------------------------------
export type FundingSourceLinkType = 'plaid' | 'card';

export type FundingSourceLink = {
  /** Portal URL the partner hands to the business. The scoped session JWT is embedded as `?token=`. */
  url: string;
  expiration: string;
};

/**
 * POST /v2/accounts/link (Melio-Entity-Id header) — mint a scoped link (partner api-key).
 * The business opens the returned `url`, which lands on the portal and runs the Plaid/card flow.
 */
export async function createFundingSourceLink(
  ctx: RequestContext,
  entityId: string,
  type: FundingSourceLinkType,
  signal?: AbortSignal
): Promise<FundingSourceLink> {
  const res = await request<{ data: FundingSourceLink }>(ctx, '/accounts/link', {
    method: 'POST',
    headers: { 'Melio-Entity-Id': entityId },
    body: { type },
    signal,
  });
  return res.data;
}

/**
 * POST /v2/accounts (Melio-Entity-Id header) authenticated with the scoped session token
 * (Bearer) rather than the partner api-key — used by the business-facing add-funding-source
 * flow, which never holds the partner api-key. A funding source is an `internal` account.
 */
export async function submitPlaidFundingSourceWithSession(
  env: RequestContext['env'],
  sessionToken: string,
  entityId: string,
  publishableKey: string,
  details: { plaidToken: string; plaidAccountId: string; nickname?: string },
  signal?: AbortSignal
): Promise<{ id: string }> {
  const { nickname, ...plaid } = details;
  const sonarToken = await getSonarSessionToken(env, publishableKey, entityId);
  return http<{ id: string }>(env, '/accounts', {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}`, 'Melio-Entity-Id': entityId, ...sonarHeader(sonarToken) },
    body: { ownershipType: 'internal', type: 'plaid', ...(nickname ? { nickname } : {}), details: plaid },
    signal,
  });
}

/**
 * POST /v2/accounts/{accountId}/verify/link (Melio-Entity-Id header) — mint a scoped link (partner
 * api-key) that lets the business reopen Plaid in update mode to complete micro-deposit
 * verification for an existing unverified Plaid account.
 */
export async function createAccountVerifyLink(
  ctx: RequestContext,
  entityId: string,
  accountId: string,
  signal?: AbortSignal
): Promise<FundingSourceLink> {
  const res = await request<{ data: FundingSourceLink }>(ctx, `/accounts/${accountId}/verify/link`, {
    method: 'POST',
    headers: { 'Melio-Entity-Id': entityId },
    signal,
  });
  return res.data;
}

/**
 * GET /v2/lookup/address/suggestions — public address autocomplete (no entity scope).
 * Used by the business-facing card flow's address autocomplete (no partner api-key in the browser).
 */
export async function lookupAddressesWithSession(
  env: RequestContext['env'],
  sessionToken: string,
  entityId: string,
  q: string,
  signal?: AbortSignal
): Promise<AddressSuggestion[]> {
  const path = `/lookup/address/suggestions?q=${encodeURIComponent(q)}&type=domestic`;
  const res = await http<{ data: AddressSuggestion[] }>(env, path, {
    headers: { Authorization: `Bearer ${sessionToken}` },
    signal,
  });
  return res?.data ?? [];
}

export type CardFundingSourceDetails = {
  /** Vault token for the card (Basis Theory token id). */
  token: string;
  cardBin: string;
  expirationMonth: string;
  expirationYear: string;
  lastFourDigits: string;
  cardOwner: { firstName: string; lastName: string };
  address: { line1: string; line2?: string; city: string; state: string; postalCode: string };
};

/**
 * POST /v2/accounts (Melio-Entity-Id header) for a card, authenticated with the scoped session
 * token (Bearer). The PAN is tokenized client-side via Basis Theory — only the vault token reaches us.
 */
export async function submitCardFundingSourceWithSession(
  env: RequestContext['env'],
  sessionToken: string,
  entityId: string,
  publishableKey: string,
  details: CardFundingSourceDetails,
  signal?: AbortSignal
): Promise<{ id: string }> {
  const sonarToken = await getSonarSessionToken(env, publishableKey, entityId);
  return http<{ id: string }>(env, '/accounts', {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}`, 'Melio-Entity-Id': entityId, ...sonarHeader(sonarToken) },
    body: { ownershipType: 'internal', type: 'card', details: { tokenProvider: 'basistheory', ...details } },
    signal,
  });
}
